import CardTitleWithIcon from "@/components/company/card-title-with-icon";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import { getExactGoogleReviewCount } from "@/lib/company-reviews-count";
import type { CompanyDTO, CompanyReviewTopic } from "@shaarp/common";
import { StarIcon } from "lucide-react";

/** The themes shown: Google's own list is short, a few dozen at most. */
const MAX_TOPICS = 12;

type GoogleReviews = CompanyDTO["reviews"]["google"];

function GoogleRatingHeadline({
  rating,
  google,
}: {
  readonly rating: number;
  readonly google: GoogleReviews;
}) {
  const { t, currentLanguage } = useTranslation();
  const exactCount = getExactGoogleReviewCount(google);
  const countLabel =
    exactCount != null && exactCount > 0
      ? t("company.reviews.basedOn", { count: exactCount })
      : google.reviewCountRange &&
        t("company.reviews.basedOnRange", { range: google.reviewCountRange });

  const formattedRating = new Intl.NumberFormat(currentLanguage, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(rating);

  return (
    <div className="flex items-center gap-3">
      <span className="text-3xl font-semibold tabular-nums">
        {formattedRating}
      </span>
      <div className="flex flex-col gap-0.5">
        <div
          className="flex items-center gap-0.5"
          role="img"
          aria-label={t("company.reviews.ratingOutOfFive", {
            rating: formattedRating,
          })}
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <StarIcon
              key={star}
              className={
                star <= Math.round(rating)
                  ? "size-4 fill-amber-400 text-amber-400"
                  : "size-4 text-muted-foreground/40"
              }
            />
          ))}
        </div>
        {countLabel && (
          <span className="text-xs text-muted-foreground">{countLabel}</span>
        )}
      </div>
    </div>
  );
}

function GoogleReviewTopics({
  topics,
}: {
  readonly topics: readonly CompanyReviewTopic[];
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-muted-foreground">
        {t("company.reviews.topics")}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {topics.map((topic) => (
          <Badge
            key={topic.keyword}
            variant="outline"
            title={t("company.reviews.topicMentions", {
              count: topic.mentions,
            })}
          >
            {topic.keyword}
            <span className="text-muted-foreground">{topic.mentions}</span>
          </Badge>
        ))}
      </div>
    </div>
  );
}

/**
 * Google's rating of the company's place, its review count, and the themes
 * Google groups the reviews by — the reviews themselves are in the news
 * widget. `withRating={false}` beside a card that already shows the rating:
 * the themes alone, or nothing.
 */
export default function CardGoogleReviews({
  company,
  withRating = true,
}: {
  readonly company: CompanyDTO;
  readonly withRating?: boolean;
}) {
  const { t } = useTranslation();
  const google = company.reviews?.google;
  const topics = (google?.topics ?? []).slice(0, MAX_TOPICS);
  const rating = withRating ? (google?.rating ?? null) : null;
  if (!google || (rating == null && topics.length === 0)) return null;

  return (
    <Card className="flex flex-col min-w-0 overflow-hidden">
      <CardHeader>
        <CardTitleWithIcon icon={StarIcon} title={t("company.reviews.googleTitle")} />
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {rating != null && (
          <GoogleRatingHeadline rating={rating} google={google} />
        )}
        {topics.length > 0 && <GoogleReviewTopics topics={topics} />}
      </CardContent>
    </Card>
  );
}
